/**
 * AccountingSummarySubTab.jsx — Muhasebe FAZ 3.6, "Özet" alt sekmesi.
 *
 * PLAN §11.2: mevcut `PaymentSummaryCards` (eskiden AdminStudentDetailPage.jsx
 * içinde local bileşendi) + `StudentPaymentsTab` içeriği AYNEN korunur —
 * mantık kopyalanmadı, sadece taşındı.
 */
import { useLanguage } from '../../../contexts/LanguageContext';
import { fmtMoneyAccounting } from '../../../utils/money';
import StudentPaymentsTab from '../StudentPaymentsTab';

// ── Ozet kart (Muhasebe sekmesi ustu) ─────────────────────────────────────────
// AdminStudentDetailPage.jsx'ten AYNEN taşındı (Faz 3.6).
function PaymentSummaryCards({ detail }) {
  const { t, lang } = useLanguage();

  // Borc/odeme ozeti FATURA verisinden gelir (backend: invoice_billed/collected/outstanding).
  // payments yalnizca tahsilat kayitlaridir — borc kaynagi degildir.
  const totalBilled = detail?.invoice_billed || 0;
  const totalPaid = detail?.invoice_collected || 0;
  const remaining = detail?.invoice_outstanding ?? (totalBilled - totalPaid);
  // FAZ 1-B (Seçenek C, faiz) — ayrı alan, `remaining`/toplamların İÇİNE
  // katılmaz (bkz. DebtLedgerSubTab.jsx aynı disiplin).
  const totalInterest = detail?.invoice_interest_collected || 0;
  // FAZ 1-B (Seçenek B, iade) — iade geçmişi varsa toplamlar 0'a inebilir
  // (20.000 satış + 20.000 tahsilat + 20.000 tam iade -> ikisi de 0) ama
  // öğrencinin gerçek bir hareketi vardır; aşağıdaki erken-çıkış guard'ı
  // bunu da hesaba katar (bkz. hasActivity).
  const hasRefundHistory = (detail?.invoices || []).some((inv) => inv.invoice_type === 'refund');

  // Eskiden yalniz billed/paid/installments kontrol edilirdi — iade
  // nedeniyle toplamlar 0'a inince kartlar tamamen kayboluyordu (bilgi
  // kaybi). Faiz > 0 veya iade gecmisi varsa da "gosterilecek bir sey var".
  const hasActivity = totalBilled !== 0
    || totalPaid !== 0
    || (detail?.installments || []).length > 0
    || totalInterest > 0
    || hasRefundHistory;
  if (!hasActivity) return null;

  // Faz 3.7a: yerel Intl.NumberFormat cekirdek `fmtMoneyAccounting`'e tasindi
  // (davranis degisikligi kabul: artik her zaman 2 ondalik + "₺0,00", plan §11.3).
  const fmt = (n) => fmtMoneyAccounting(n, lang);

  // FAZ 1-B (Seçenek B, iade) — `remaining` negatif olabilir (öğrenci
  // alacaklı). Üç durum ayrı renk + ayrı etiket taşır; DebtLedgerSubTab.jsx
  // `kalan_bakiye` için AYNI kararı uygular — iki ekran tutarlı kalsın diye
  // aynı i18n anahtarı (`accounting.debt_ledger.balance_creditor`) paylaşılır.
  let remainingLabel = t('accounting.columns.remaining');
  let remainingColor = 'text-emerald-600';
  let remainingValue = remaining;
  if (remaining > 0) {
    remainingColor = 'text-amber-600';
  } else if (remaining < 0) {
    remainingColor = 'text-rose-600';
    remainingLabel = t('accounting.debt_ledger.balance_creditor');
    remainingValue = Math.abs(remaining);
  }

  // i18n: 'total_billed' bu karta ozel (veri kaynagi debt_ledger'dan farkli, ayni yazi
  // ama farkli anahtar — bkz. LanguageContext.js yorumu). paid/remaining icin mevcut
  // genel anahtarlar tekrar kullanildi (yeni orphan yaratilmadi).
  const cards = [
    { label: t('accounting.summary.total_billed'), value: fmt(totalBilled), color: 'text-rose-600' },
    { label: t('admin.inv.paid_amount'), value: fmt(totalPaid), color: 'text-emerald-600' },
    { label: remainingLabel, value: fmt(remainingValue), color: remainingColor },
  ];

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border-2 border-[var(--border-light)] bg-white p-3 text-center">
            <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-1">{c.label}</p>
            <p className={`text-lg font-display font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      {/* FAZ 1-B (Seçenek C, faiz): 0 iken gösterilmez, DebtLedgerSubTab.jsx
          "info" tonu (sky) ile AYNI görsel disiplin — toplamların bir
          parçası gibi görünmez, ayrı bilgi kartı. */}
      {totalInterest > 0 && (
        <div
          className="rounded-xl border-2 border-sky-200 bg-sky-50/60 p-3 text-center mb-4 max-w-xs"
          data-testid="accounting-summary-interest-card"
        >
          <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-1">
            {t('accounting.debt_ledger.total_interest')}
          </p>
          <p className="text-lg font-display font-bold text-sky-600">{fmt(totalInterest)}</p>
          <p className="text-[9px] leading-snug text-[var(--text-muted)] mt-1">
            {t('accounting.debt_ledger.interest_hint')}
          </p>
        </div>
      )}
    </div>
  );
}

export default function AccountingSummarySubTab({ detail }) {
  return (
    <div>
      <PaymentSummaryCards detail={detail} />
      <StudentPaymentsTab detail={detail} />
    </div>
  );
}
